import { useCallback, useMemo, useState } from "react";
import { Clipboard, RotateCcw, Save, Sparkles, Wand2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { HybridFormField } from "@/components/HybridFormField";
import { VisualTranslationHint } from "@/components/VisualTranslationHint";
import { usePromptLibrary } from "@/hooks/usePromptLibrary";
import { copyTextSafely } from "@/lib/clipboard";
import { getConcreteSceneDescription, getPrimaryUserInput, normalizeFinnishPromptText, normalizeUserInputToEnglish, sanitizeEnglishPrompt } from "@/lib/languageNormalizer";
import { appendFinnishVisualGuidance } from "@/lib/visualPromptDefaults";

const OTHER_VALUE = "Muu, mikä?";

const aiToolField = {
  id: "aiTool",
  label: "Valitse AI-työkalu",
  type: "select",
  optionGroups: [
    { label: "Teksti ja ideointi", options: ["ChatGPT", "Claude", "Gemini"] },
    { label: "AI-kuvat", options: ["ChatGPT Image / GPT Image", "Gemini / Nano Banana", "Midjourney", "Adobe Firefly", "FLUX", "Stable Diffusion"] },
    { label: "AI-videot", options: ["Google Veo", "Kling AI", "Runway", "Pika", "Luma", "Higgsfield"] },
    { label: "Avatarit ja puhevideot", options: ["Heygen"] },
    { label: "Ääni ja musiikki", options: ["ElevenLabs", "Suno"] },
  ],
  options: [OTHER_VALUE],
  helpText: "Valitse työkalu, johon haluat viedä valmiin promptin.",
};

const fields = [
  {
    id: "idea",
    label: "Kirjoita ideasi lyhyesti",
    type: "textarea",
    placeholder: "Esim. Haluan tehdä realistisen videon naisesta, joka kävelee rannalla auringonlaskussa ja katsoo kameraan.",
    helpText: "Kirjoita tähän vapaasti, mitä haluat tehdä. Ei haittaa, vaikka idea olisi vielä keskeneräinen.",
  },
  aiToolField,
  {
    id: "contentType",
    label: "Sisältötyyppi",
    type: "select",
    options: ["Somepostaus", "Lyhytvideon käsikirjoitus", "UGC-videon käsikirjoitus", "AI-kuva", "AI-video", "Image-to-video", "Text-to-video", "AI-avatar-video", "Ääniprompti", "Musiikkiprompti", "Mainosteksti", "Sähköposti", "Myyntisivun tekstiosio", "Sisältöideat", OTHER_VALUE],
    helpText: "Valitse, millaiseksi lopputulokseksi idea muutetaan.",
  },
  { id: "attachedImage", label: "Käytetäänkö liitettyä kuvaa?", type: "select", options: ["Ei", "Kyllä, referenssinä", "Kyllä, lähtökuvana"], helpText: "Valitse kyllä, jos viet työkaluun myös kuvan promptin lisäksi." },
  {
    id: "aspectRatio",
    label: "Kuvasuhde",
    type: "select",
    options: ["9:16 — TikTok, Reels, Shorts, Stories", "1:1 — Instagram-postaus", "4:5 — Instagram-feed", "16:9 — YouTube, verkkosivut", "3:2 — valokuvamainen kuva", "21:9 — cinematic / banneri", OTHER_VALUE],
    helpText: "Näytetään kuva- ja videoprompteissa. Valitse formaatti käyttötarkoituksen mukaan.",
  },
];

const initialForm = {
  idea: "",
  aiTool: "",
  aiTool__other: "",
  contentType: "",
  contentType__other: "",
  attachedImage: "Ei",
  aspectRatio: "9:16 — TikTok, Reels, Shorts, Stories",
  aspectRatio__other: "",
};

const exampleForm = {
  idea: "Haluan tehdä realistisen videon naisesta, joka kävelee rannalla auringonlaskussa, katsoo kameraan ja hymyilee kevyesti. Videon pitää tuntua aidolta somevideolta, ei liian tuotetulta mainokselta.",
  aiTool: "Kling AI",
  contentType: "Image-to-video",
  attachedImage: "Kyllä, lähtökuvana",
  aspectRatio: "9:16 — TikTok, Reels, Shorts, Stories",
};

const visualTypes = ["AI-kuva", "AI-video", "Image-to-video", "Text-to-video"];
const imageContentTypes = ["AI-kuva"];
const videoContentTypes = ["AI-video", "Image-to-video", "Text-to-video"];
const textTools = ["chatgpt", "claude", "gemini"];
const imageTools = ["chatgpt image", "gpt image", "nano banana", "midjourney", "adobe firefly", "flux", "stable diffusion"];
const videoTools = ["google veo", "veo", "kling", "runway", "pika", "luma", "higgsfield"];
const audioTools = ["elevenlabs", "suno"];

const getFieldValue = (form, key) => {
  const current = form[key];
  const other = (form[`${key}__other`] || "").trim();
  if (Array.isArray(current)) return [...current.filter((item) => item !== OTHER_VALUE), other].filter(Boolean).join(", ");
  if (current === OTHER_VALUE) return other;
  return current || "";
};

const aspectValue = (input) => String(input || "").match(/\d+\s*:\s*\d+/)?.[0].replace(/\s/g, "") || String(input || "").trim();

const includesAny = (value, needles) => needles.some((needle) => value.includes(needle));

const isTextTool = (tool) => {
  const selected = tool.toLowerCase();
  return textTools.includes(selected);
};

const isImageTool = (tool) => includesAny(tool.toLowerCase(), imageTools);

const isVideoTool = (tool) => includesAny(tool.toLowerCase(), videoTools);

const isImagePrompt = (tool, contentType) => imageContentTypes.includes(contentType) || isImageTool(tool);

const isVideoPrompt = (tool, contentType) => videoContentTypes.includes(contentType) || isVideoTool(tool);

const isAudioOrMusicPrompt = (tool, contentType) => audioTools.some((item) => tool.toLowerCase().includes(item)) || ["Ääniprompti", "Musiikkiprompti"].includes(contentType);

const avoidTranslations = {
  "Tekoälymäinen lopputulos": "an artificial AI-looking result",
  "Liian kiillotettu tyyli": "an overly polished style",
  "Muovinen iho": "plastic skin",
  "Ylisiloteltu ulkonäkö": "overly smooth beauty-filter skin",
  "Epäluonnollinen liike": "unnatural movement",
  "Kasvojen vääristyminen": "face distortion",
  "Sormien vääristyminen": "distorted fingers",
  "Hahmon identiteetin muuttuminen": "changes to the subject's identity",
  Korporaatiojargon: "corporate jargon",
  "Liian mainosmainen kieli": "overly promotional language",
  "Liika hype": "excessive hype",
  "Epäuskottavat lupaukset": "unbelievable promises",
};

const translateAvoid = (avoid) =>
  String(avoid || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => avoidTranslations[item] || item)
    .join(", ");

const outputText = (value, context = "generic") => normalizeUserInputToEnglish(value, context);

const contentTypeLabel = (contentType) => outputText(contentType, "contentType").toLowerCase();

const cleanEnglishPromptText = (input) =>
  sanitizeEnglishPrompt(input)
    .replace(/\bShow a woman looks at the camera and smiles softly\b/gi, "Show the woman looking at the camera and smiling softly")
    .replace(/\bShow a woman looks\b/gi, "Show the woman looking")
    .replace(/\bShow woman looks\b/gi, "Show the woman looking")
    .trim();

const concreteNaturalMovement = (scene = "") => {
  const context = String(scene || "").toLowerCase();
  if (context.includes("lake") || context.includes("lakeside") || context.includes("dock") || context.includes("summer landscape")) {
    return "the person gently turns their head toward the lake, breathes naturally, and the hair or clothing moves slightly in the summer breeze";
  }
  if (context.includes("beach") || context.includes("sunset")) {
    return "the person gently turns their head toward the horizon, breathes naturally, and the hair or clothing moves slightly in the sea breeze";
  }
  if (context.includes("forest") || context.includes("campfire")) {
    return "the person gently shifts their posture, breathes naturally, and makes a small realistic hand or head movement while the environment stays stable";
  }
  return "the person makes a subtle natural movement, such as gently turning their head, breathing naturally, and letting the hair or clothing move slightly";
};

const videoTypePhrase = (contentType) => {
  const normalized = contentTypeLabel(contentType).replace(/^a\s+/, "").replace(/^realistic\s+/, "");
  if (normalized.includes("image-to-video")) return "image-to-video clip";
  if (normalized.includes("text-to-video")) return "text-to-video clip";
  if (normalized.includes("video")) return normalized.includes("clip") ? normalized : `${normalized} clip`;
  return "video clip";
};

const inferredClipAction = (idea) => {
  const normalized = cleanEnglishPromptText(getConcreteSceneDescription(idea, "idea"));
  if (normalized.includes("campfire") && normalized.includes("sausage")) return "she gently moves the stick over the fire and gives a soft natural smile toward the camera";
  if (normalized.includes("misty meadow")) return "the character slowly turns around, breathes naturally and sighs softly while the atmosphere stays calm";
  return concreteNaturalMovement(normalized);
};

const videoToolReadySentence = (tool, attachedImage) => {
  const selected = String(tool || "").toLowerCase();
  const usesImage = String(attachedImage || "").toLowerCase().includes("kyllä");
  if (selected.includes("higgsfield")) return "Optimize for Higgsfield with a natural social-media-style lifestyle feel while keeping movement controlled and realistic.";
  if (selected.includes("kling")) return `Optimize for Kling AI as a ${usesImage ? "controlled image-to-video" : "physically believable video"} prompt with stable identity, subtle natural motion, no flicker, no morphing and no distorted hands.`;
  if (selected.includes("veo")) return "Optimize for Google Veo with cinematic but realistic camera movement, clear temporal continuity, physically plausible action and consistent lighting.";
  if (selected.includes("runway")) return "Optimize for Runway with one precise shot, one main action, one camera movement and restrained motion so faces, hands and background details stay stable.";
  return videoToolSentence(tool, attachedImage, "en");
};

const referenceSentence = (attachedImage, language, contentType) => {
  const lower = attachedImage.toLowerCase();
  if (!lower.includes("kyllä")) return language === "fi" ? "" : "";
  const isStartingImage = lower.includes("lähtökuvana") || contentType === "Image-to-video";
  if (language === "fi") {
    return isStartingImage
      ? "Käytä liitettyä kuvaa lähtökuvana ja säilytä kohteen identiteetti, kasvonpiirteet, vaatetus, mittasuhteet ja tärkeät taustan yksityiskohdat luonnollisesti. Muuta vain pyydetyt elementit."
      : "Käytä liitettyä kuvaa visuaalisena referenssinä ja säilytä kohteen identiteetti sekä keskeiset kasvonpiirteet luonnollisesti. Muuta vain pyydetyt elementit.";
  }
  return isStartingImage
    ? "Use the attached image as the starting frame and preserve the subject's identity, facial features, clothing, proportions and key background details naturally. Change only the requested elements."
    : "Use the attached image as a visual reference and preserve the subject's identity and key facial features naturally. Change only the requested elements.";
};

const aspectSentence = (aspectRatio, language, type) => {
  const ratio = aspectValue(aspectRatio);
  if (!ratio) return "";
  if (language === "fi") return type === "video" ? `Tee video kuvasuhteessa ${ratio}.` : `Käytä kuvasuhdetta ${ratio}.`;
  return type === "video" ? `Create the video in a ${ratio} aspect ratio.` : `Use a ${ratio} aspect ratio.`;
};

const imageToolSentence = (tool, aspectRatio, language) => {
  const selected = tool.toLowerCase();
  const ratio = aspectValue(aspectRatio);
  if (selected.includes("midjourney")) return language === "fi" ? `Pidä ohje Midjourneylle sopivana visuaalisena promptina ja lisää loppuun --ar ${ratio || "9:16"}.` : `Keep this as a Midjourney-ready visual prompt with compact style keywords, camera feel, lighting and composition${ratio ? `, ending with --ar ${ratio}` : ""}.`;
  if (selected.includes("nano")) return language === "fi" ? "Muotoile tämä Gemini / Nano Banana -tyyliin selkeänä kuvanluonti- tai kuvanmuokkauspyyntönä." : "Format this for Gemini / Nano Banana as a clear image generation or image editing request.";
  if (selected.includes("firefly")) return language === "fi" ? "Pidä prompti Adobe Fireflylle sopivana, selkeänä kaupallisen turvallisena visuaalisena kuvauksena." : "Keep the prompt Adobe Firefly-friendly: clear, commercially safe and visually specific.";
  if (selected.includes("flux")) return language === "fi" ? "Pidä prompti FLUXille sopivana fotorealistisena kuvauksena, jossa on selkeä kohde, valo, ympäristö ja materiaalit." : "Keep the prompt FLUX-friendly with a photorealistic subject, clear lighting, environment and material detail.";
  if (selected.includes("stable diffusion")) return language === "fi" ? "Pidä prompti Stable Diffusionille sopivana: selkeä positiivinen prompti ja negatiiviset ohjeet samassa tekstissä." : "Keep the prompt Stable Diffusion-friendly: a clear positive prompt with negative constraints included naturally.";
  if (selected.includes("gpt image") || selected.includes("chatgpt image")) return language === "fi" ? "Kirjoita tämä ChatGPT Image / GPT Image -työkalulle suoraan toteutettavana kuvaus- tai muokkauspyyntönä." : "Write this as a direct ChatGPT Image / GPT Image generation or editing request.";
  return "";
};

const videoToolSentence = (tool, attachedImage, language) => {
  const selected = tool.toLowerCase();
  const usesImage = attachedImage.toLowerCase().includes("kyllä");
  if (selected.includes("kling")) return language === "fi" ? `${usesImage ? "Käsittele tämä Kling AI:ssa image-to-video-promptina." : "Pidä Kling AI -video fyysisesti uskottavana."} Painota identiteetin säilymistä, pientä luonnollista liikettä, vakaita kasvoja ja flickeröinnin välttämistä.` : `${usesImage ? "Treat this as a Kling AI image-to-video prompt." : "Keep the Kling AI video physically believable."} Emphasize identity preservation, subtle natural motion, stable faces and no flickering.`;
  if (selected.includes("veo")) return language === "fi" ? "Muotoile tämä Google Veolle elokuvallisena mutta realistisena kohtauksena, jossa on selkeä kameraliike ja ajallinen jatkuvuus." : "Format this for Google Veo as a cinematic but realistic scene with clear camera movement and temporal continuity.";
  if (selected.includes("runway")) return language === "fi" ? "Muotoile tämä Runwaylle tarkkana shot-kuvauksena, jossa on yksi pääliike, yksi kameraliike ja selkeät jatkuvuusrajat." : "Format this for Runway as a precise shot description with one main action, one camera move and clear continuity limits.";
  if (selected.includes("pika")) return language === "fi" ? "Pidä Pika-prompti lyhyenä, selkeänä ja liikettä painottavana, jotta muutos pysyy hallittuna." : "Keep the Pika prompt concise, clear and motion-focused so the transformation stays controlled.";
  if (selected.includes("luma")) return language === "fi" ? "Muotoile tämä Lumalle realistisena kohtauksena, jossa liike, valon jatkuvuus ja kameran rytmi ovat selkeitä." : "Format this for Luma as a realistic scene with clear motion, light continuity and camera rhythm.";
  if (selected.includes("higgsfield")) return language === "fi" ? "Muotoile tämä Higgsfieldille somehenkisenä lifestyle-videona, jossa kameraliike on hallittu ja identiteetti säilyy." : "Format this for Higgsfield as a social-media-style lifestyle video with controlled camera movement and identity preservation.";
  return "";
};

const toolInstruction = (tool, contentType, attachedImage, aspectRatio, language = "fi") => {
  const selected = tool.toLowerCase();
  const type = contentType.toLowerCase();
  const ratio = aspectValue(aspectRatio);
  const usesImage = attachedImage.toLowerCase().includes("kyllä");
  if (language === "en") {
    if (selected.includes("midjourney")) return `Optimize for Midjourney: use compact visual keywords, camera style, lighting, composition, realistic texture and a clear style direction.${ratio ? ` Add --ar ${ratio} at the end.` : ""}`;
    if (selected.includes("nano") || selected.includes("gemini / nano")) return `Optimize for Gemini / Nano Banana: write a clear image generation or editing request that preserves natural faces, identity, product and environment consistency${usesImage ? " from the attached image" : ""}.`;
    if (selected.includes("gpt image") || selected.includes("chatgpt image")) return "Optimize for ChatGPT Image / GPT Image: clearly state what to create, what to preserve, what to change and what realistic final result is expected.";
    if (selected.includes("kling")) return `Optimize for Kling AI: ${usesImage || type.includes("image-to-video") ? "treat this as an image-to-video prompt and preserve the starting image identity, face, clothing, product and background" : "keep the scene physically believable"}. Use subtle natural motion and avoid flicker, morphing, face distortion and extra fingers.`;
    if (selected.includes("veo")) return "Optimize for Google Veo: make the scene cinematic but realistic, with camera movement, temporal continuity, lighting and physically plausible action clearly defined.";
    if (selected.includes("runway")) return "Optimize for Runway: provide a precise shot description, duration, one main action, one camera move and clear continuity limits so faces, hands and background do not drift.";
    if (selected.includes("higgsfield")) return "Optimize for Higgsfield: emphasize social-media-style camera motion, controlled energy, lifestyle or fashion style and identity preservation.";
    if (selected.includes("heygen")) return "Optimize for Heygen: make the prompt suitable for an avatar or talking video, using short speakable sentences, natural pauses and believable presentation rhythm.";
    if (selected.includes("elevenlabs")) return "Optimize for ElevenLabs: define voice tone, speaking speed, pauses, emotion, clear articulation and possible sound environment. Avoid robotic reading.";
    if (selected.includes("suno")) return "Optimize for Suno: provide separate Style and Lyrics sections; the Style section should include genre, mood, tempo, instruments and production feel, and the Lyrics section should include lyrics or the instruction instrumental, no vocals.";
    if (selected.includes("chatgpt") || selected.includes("claude") || selected.includes("gemini")) return "Write this as a structured text prompt: role, goal, context, style, format, constraints and final output. Ask for a concrete, natural and directly usable response.";
    return tool ? `Adapt the prompt for ${tool}: keep the instruction clear, concrete and suitable for the tool's workflow.` : "Make the prompt clear, concrete and easy to copy.";
  }
  if (selected.includes("midjourney")) return `Optimoi Midjourneylle: käytä tiiviitä visuaalisia avainsanoja, kameratyyliä, valaistusta, sommittelua, realistista tekstuuria ja selkeää tyylisuuntaa.${ratio ? ` Lisää loppuun --ar ${ratio}.` : ""}`;
  if (selected.includes("nano") || selected.includes("gemini / nano")) return `Optimoi Gemini / Nano Banana -kuvalle: kirjoita selkeä kuvaus- tai muokkauspyyntö, jossa säilytetään luonnolliset kasvot, identiteetti, tuote ja ympäristö${usesImage ? " liitetystä kuvasta" : ""}.`;
  if (selected.includes("gpt image") || selected.includes("chatgpt image")) return "Optimoi ChatGPT Image / GPT Image -työkaluun: kerro selkeästi mitä luodaan, mitä säilytetään, mitä muutetaan ja millainen realistinen lopputulos halutaan.";
  if (selected.includes("kling")) return `Optimoi Kling AI:lle: ${usesImage || type.includes("image-to-video") ? "käsittele tämä image-to-video-promptina ja säilytä lähtökuvan identiteetti, kasvot, vaatetus, tuote ja tausta" : "pidä kohtaus fyysisesti uskottavana"}. Käytä pientä luonnollista liikettä ja vältä flickeröintiä, morphingia, kasvojen vääristymistä sekä ylimääräisiä sormia.`;
  if (selected.includes("veo")) return "Optimoi Google Veolle: tee kohtauksesta elokuvallinen mutta realistinen, määrittele kameran liike, ajallinen jatkuvuus, valaistus ja fyysisesti mahdollinen toiminta.";
  if (selected.includes("runway")) return "Optimoi Runwaylle: anna tarkka shot-kuvaus, kesto, yksi pääliike, yksi kameraliike ja selkeät jatkuvuusrajat, jotta kasvot, kädet ja tausta eivät ala muuttua.";
  if (selected.includes("higgsfield")) return "Optimoi Higgsfieldille: painota somehenkistä kameraliikettä, hallittua energiaa, lifestyle- tai fashion-tyyliä ja identiteetin säilymistä.";
  if (selected.includes("heygen")) return "Optimoi Heygenille: tee promptista avatarille tai puhuvalle videolle sopiva, käytä lyhyitä puhuttavia lauseita, luonnollisia taukoja ja uskottavaa esiintymisrytmiä.";
  if (selected.includes("elevenlabs")) return "Optimoi ElevenLabsille: määrittele äänen sävy, puhenopeus, tauot, tunne, selkeä artikulaatio ja mahdollinen äänimaisema. Vältä robottimaista lukemista.";
  if (selected.includes("suno")) return "Optimoi Sunolle: anna erikseen Style-kenttään genre, tunnelma, tempo, instrumentit ja tuotannollinen tuntuma sekä Lyrics-kenttään lauluteksti tai ohje instrumental, no vocals.";
  if (selected.includes("chatgpt") || selected.includes("claude") || selected.includes("gemini")) return "Kirjoita rakenteellisena tekstipromptina: rooli, tavoite, konteksti, tyyli, formaatti, rajoitteet ja lopputulos. Pyydä konkreettista, luonnollista ja suoraan käytettävää vastausta.";
  return tool ? `Sovita prompti työkalulle ${tool}: pidä ohje selkeänä, konkreettisena ja työkalun käyttötapaan sopivana.` : "Tee promptista selkeä, konkreettinen ja helposti kopioitava.";
};

const buildDirectImagePrompt = ({ idea, tool, attachedImage, aspectRatio }) => {
  const scene = normalizeFinnishPromptText(idea);
  const ratio = aspectValue(aspectRatio);
  const finnish = `Luo erittäin realistinen ja luonnollisen näköinen kuva aiheesta: ${scene}. ${referenceSentence(attachedImage, "fi", "AI-kuva")} ${ratio ? `Käytä kuvasuhdetta ${ratio}.` : ""} Kuvan tulee tuntua aidolta kameralla otetulta hetkeltä, ei liian asetellulta mainoskuvalta. Painota realistista ympäristöä, luonnollista valoa, aitoa tunnelmaa, realistista ihon tekstuuria, luonnollisia käsiä, uskottavia mittasuhteita ja pieniä inhimillisiä yksityiskohtia. ${imageToolSentence(tool, aspectRatio, "fi")}`.replace(/\s+/g, " ").trim();
  return appendFinnishVisualGuidance(finnish);
};

const buildDirectVideoPrompt = ({ idea, tool, contentType, attachedImage, aspectRatio }) => {
  const scene = normalizeFinnishPromptText(idea);
  const ratio = aspectValue(aspectRatio);
  const finnish = [
    `Luo erittäin realistinen 5 sekunnin ${contentType === "Text-to-video" ? "text-to-video-klippi" : "image-to-video- tai videoklippi"}.`,
    ratio ? `Käytä kuvasuhdetta ${ratio}.` : "",
    referenceSentence(attachedImage, "fi", contentType),
    `Videossa näkyy: ${scene}.`,
    "Videon aikana tapahtuu vain pieni, luonnollinen ja uskottava liike, joka tukee käyttäjän ideaa.",
    "Käytä kevyttä dokumentaarista handheld-kameran liikettä tai muuta rauhallista kameraliikettä.",
    "Käytä realistista ja johdonmukaista valaistusta sekä rauhallista, aitoa tunnelmaa.",
    toolInstruction(tool, contentType, attachedImage, aspectRatio, "fi"),
  ].filter(Boolean).join(" ");
  return appendFinnishVisualGuidance(finnish);
};

const buildDirectAudioPrompt = ({ idea, tool, contentType, avoid }) => {
  const isSuno = tool.toLowerCase().includes("suno") || contentType === "Musiikkiprompti";
  if (isSuno) {
    return `Style:
${normalizeFinnishPromptText(idea)}. Kuvaa genre, tunnelma, tempo, instrumentit, energia, tuotannollinen tuntuma ja rakenne mahdollisimman selkeästi.

Lyrics:
${contentType === "Musiikkiprompti" ? "Kirjoita sanat vain, jos laulu on tarpeen. Muuten käytä ohjetta: instrumental, no vocals." : "instrumental, no vocals."}

Vältä:
${normalizeFinnishPromptText(translateAvoid(avoid)) || "liian geneeristä taustamusiikkia, liian dramaattisia rumpuja, sekavaa genrekuvausta ja kovia ääniä"}`;
  }
  return `Luo luonnollinen ääni- tai äänitehosteprompti tämän idean pohjalta: ${normalizeFinnishPromptText(idea)}. Määrittele äänen sävy, rytmi, tauot, tunne, selkeys, ääntäminen ja mahdollinen äänimaisema. Vältä ${normalizeFinnishPromptText(translateAvoid(avoid)) || "robottimaista lukemista, liian kovaa ääntä, epäselvää artikulaatiota ja ylidramaattista ilmaisua"}.`;
};

const buildStructuredIdeaPrompt = ({ idea, tool, contentType, attachedImage, aspectRatio, avoid }) => `Rooli:
Toimi kokeneena promptisuunnittelijana, joka muuttaa keskeneräisen idean selkeäksi, luonnolliseksi ja työkalulle sopivaksi promptiksi.

Käyttäjän idea:
${normalizeFinnishPromptText(idea)}

Tavoite:
Muuta idea käyttövalmiiksi promptiksi sisältötyypille: ${contentType}. Promptin pitää olla suoraan kopioitavissa valittuun AI-työkaluun${tool ? `: ${tool}` : ""}.

Työkalukohtainen ohje:
${toolInstruction(tool, contentType, attachedImage, aspectRatio, "fi")}

Konteksti ja rajaukset:
${attachedImage && attachedImage !== "Ei" ? `Käyttäjä käyttää liitettyä kuvaa: ${normalizeFinnishPromptText(attachedImage)}. Säilytä sen tärkeät yksityiskohdat luonnollisesti.` : "Promptin pitää toimia ilman liitettyä kuvaa."}${aspectRatio ? ` Käytä kuvasuhdetta ${aspectValue(aspectRatio) || normalizeFinnishPromptText(aspectRatio)}.` : ""}

Tyyli ja laatu:
Tee promptista realistinen, selkeä, luonnollinen ja ei-tekoälymäinen. Lisää konkreettisia yksityiskohtia, mutta älä tee promptista liian pitkää tai mekaanista.

Rajoitteet:
${avoid ? `Vältä erityisesti: ${normalizeFinnishPromptText(translateAvoid(avoid))}.` : "Vältä geneeristä, liian kiillotettua, epäuskottavaa tai täytesanoilla rakennettua lopputulosta."}

Lopputulos:
Kirjoita valmis prompti suomeksi. Älä jätä hakasulkeita, tyhjiä kohtia tai placeholder-tekstejä. Anna vain käyttövalmis prompti ja tarvittaessa yksi lyhyt käyttöhuomio siitä, mihin se liitetään.`;

const buildIdeaPrompt = (form) => {
  const idea = getPrimaryUserInput(form, ["idea"], false) || "Käyttäjällä on keskeneräinen sisältöidea, josta pitää tehdä käyttövalmis prompti.";
  const tool = getFieldValue(form, "aiTool");
  const contentType = getFieldValue(form, "contentType") || "sisältö";
  const attachedImage = getFieldValue(form, "attachedImage");
  const aspectRatio = visualTypes.includes(contentType) ? getFieldValue(form, "aspectRatio") : "";
  const avoid = getFieldValue(form, "avoid");

  if (isImagePrompt(tool, contentType) && !isTextTool(tool) && !isVideoPrompt(tool, contentType)) {
    return buildDirectImagePrompt({ idea, tool, attachedImage, aspectRatio, avoid });
  }

  if (isVideoPrompt(tool, contentType) && !isTextTool(tool)) {
    return buildDirectVideoPrompt({ idea, tool, contentType, attachedImage, aspectRatio, avoid });
  }

  if (tool.toLowerCase().includes("suno") || tool.toLowerCase().includes("elevenlabs") || contentType === "Ääniprompti" || contentType === "Musiikkiprompti") {
    return buildDirectAudioPrompt({ idea, tool, contentType, avoid });
  }

  return buildStructuredIdeaPrompt({ idea, tool, contentType, attachedImage, aspectRatio, avoid });
};

export default function IdeaPromptPage() {
  const [form, setForm] = useState(initialForm);
  const [prompt, setPrompt] = useState("");
  const [promptName, setPromptName] = useState("");
  const { savePrompt } = usePromptLibrary();

  const visibleFields = useMemo(() => fields.filter((field) => field.id !== "aspectRatio" || visualTypes.includes(getFieldValue(form, "contentType"))), [form]);
  const selectedAiTool = getFieldValue(form, "aiTool");
  const selectedContentType = getFieldValue(form, "contentType") || "Ideasta promptiksi";
  const showVisualTranslationHint = Boolean(prompt) && (visualTypes.includes(selectedContentType) || isImageTool(selectedAiTool) || isVideoTool(selectedAiTool) || isAudioOrMusicPrompt(selectedAiTool, selectedContentType));

  const update = useCallback((key, value) => setForm((current) => ({ ...current, [key]: value })), []);
  const updateOther = useCallback((key, value) => setForm((current) => ({ ...current, [`${key}__other`]: value })), []);

  const generate = useCallback(() => setPrompt(buildIdeaPrompt(form)), [form]);
  const fillExample = useCallback(() => {
    setForm({ ...initialForm, ...exampleForm });
    setPrompt("");
    setPromptName("");
  }, []);
  const reset = useCallback(() => {
    setForm(initialForm);
    setPrompt("");
    setPromptName("");
  }, []);
  const copyPrompt = useCallback(async () => {
    const copied = await copyTextSafely(prompt);
    toast[copied ? "success" : "error"](copied ? "Prompti kopioitu!" : "Kopiointi epäonnistui. Kopioi prompti käsin tekstikentästä.");
  }, [prompt]);
  const saveCurrentPrompt = useCallback(() => savePrompt({ title: promptName, type: selectedContentType, prompt, aiTool: selectedAiTool }), [prompt, promptName, savePrompt, selectedAiTool, selectedContentType]);
  const startNew = useCallback(() => {
    setPrompt("");
    setPromptName("");
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="idea-prompt-page">
      <div className="mb-8 max-w-3xl" data-testid="idea-prompt-heading-block">
        <p className="text-sm font-extrabold uppercase text-[#D97757]" data-testid="idea-prompt-kicker">Ideasta promptiksi</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-[#2C2F2E] sm:text-5xl" data-testid="idea-prompt-title">Muuta ideasi valmiiksi promptiksi</h1>
        <p className="mt-4 text-base leading-7 text-[#5C6360]" data-testid="idea-prompt-description">Kirjoita keskeneräinenkin idea suomeksi. Studio rakentaa siitä selkeän suomenkielisen promptin kopioitavaksi.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(420px,0.85fr)]" data-testid="idea-prompt-workspace">
        <section className="rounded-[1.75rem] border border-[#E8E5D9] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-7" data-testid="idea-prompt-form-section">
          <div className="mb-5" data-testid="idea-prompt-form-heading">
            <h2 className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="idea-prompt-form-title">Täytä idean tiedot</h2>
            <p className="mt-1 text-sm leading-6 text-[#5C6360]" data-testid="idea-prompt-form-description">Voit aloittaa yhdestä lauseesta. Valinnat auttavat tekemään promptista työkalulle sopivan.</p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="idea-prompt-form-grid">
            {visibleFields.map((field) => (
              <div key={field.id} className={field.type === "textarea" || field.type === "multiselect" ? "md:col-span-2" : ""} data-testid={`idea-prompt-field-wrapper-${field.id}`}>
                <HybridFormField field={field} value={form[field.id]} onChange={(value) => update(field.id, value)} otherValue={form[`${field.id}__other`] || ""} onOtherChange={(value) => updateOther(field.id, value)} testId={`idea-prompt-${field.id}-field`} />
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="idea-prompt-form-actions">
            <button type="button" onClick={generate} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4A6753] px-6 py-4 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="idea-prompt-generate-button">
              <Wand2 className="h-4 w-4" aria-hidden="true" /> Luo prompti
            </button>
            <button type="button" onClick={fillExample} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D97757] px-6 py-4 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="idea-prompt-fill-example-button">
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Täytä esimerkillä
            </button>
            <button type="button" onClick={reset} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F5F3EC] px-6 py-4 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="idea-prompt-reset-button">
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Tyhjennä
            </button>
          </div>
        </section>

        <section className="rounded-[1.75rem] border border-[#E8E5D9] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-7" data-testid="idea-prompt-result-section">
          <div className="mb-4" data-testid="idea-prompt-result-heading">
            <p className="text-xs font-extrabold uppercase text-[#D97757]" data-testid="idea-prompt-result-kicker">Valmis prompti</p>
            <h2 className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="idea-prompt-result-title">Muokkaa ja tallenna</h2>
          </div>

          {prompt ? (
            <>
              <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} className="min-h-72 w-full resize-y rounded-[1.25rem] border border-[#4A6753]/20 bg-[#F5F3EC] p-5 text-sm leading-7 text-[#2C2F2E] outline-none focus:bg-white focus:ring-4 focus:ring-[#4A6753]/10" data-testid="idea-prompt-result-textarea" />
              {showVisualTranslationHint && <VisualTranslationHint testIdPrefix="idea-prompt-translation-hint" />}
              <div className="mt-4 rounded-[1.25rem] border border-[#E8E5D9] bg-[#FDFCF8] p-4" data-testid="idea-prompt-save-name-panel">
                <label htmlFor="idea-prompt-save-name-input" className="block text-sm font-bold text-[#2C2F2E]" data-testid="idea-prompt-save-name-label">Anna promptille nimi</label>
                <input id="idea-prompt-save-name-input" value={promptName} onChange={(event) => setPromptName(event.target.value)} placeholder="Esim. AI-kuva: realistinen somekuva yrittäjästä" className="mt-2 w-full rounded-2xl border border-transparent bg-[#F5F3EC] px-4 py-3 text-sm text-[#2C2F2E] outline-none transition-colors duration-200 placeholder:text-[#5C6360]/55 focus:border-[#4A6753]/40 focus:bg-white focus:ring-4 focus:ring-[#4A6753]/10" data-testid="idea-prompt-save-name-input" />
              </div>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="idea-prompt-result-actions">
                <button type="button" onClick={copyPrompt} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4A6753] px-4 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="idea-prompt-copy-button">
                  <Clipboard className="h-4 w-4" aria-hidden="true" /> Kopioi prompti
                </button>
                <button type="button" onClick={saveCurrentPrompt} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="idea-prompt-save-button">
                  <Save className="h-4 w-4" aria-hidden="true" /> Tallenna promptikirjastoon
                </button>
                <button type="button" onClick={startNew} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="idea-prompt-new-button">
                  <RotateCcw className="h-4 w-4" aria-hidden="true" /> Luo uusi prompti
                </button>
              </div>
            </>
          ) : (
            <div className="grid min-h-72 place-items-center rounded-[1.25rem] bg-[#F5F3EC] p-8 text-center" data-testid="idea-prompt-empty-state">
              <div>
                <Sparkles className="mx-auto mb-3 h-8 w-8 text-[#4A6753]" aria-hidden="true" />
                <p className="font-bold text-[#2C2F2E]" data-testid="idea-prompt-empty-title">Valmis prompti ilmestyy tähän</p>
                <p className="mt-1 text-sm text-[#5C6360]" data-testid="idea-prompt-empty-description">Kirjoita idea, valitse työkalu ja paina Luo prompti.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}